import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import apiRequest from '../../api/request';

/**
 * Magic Lamp — Lucky Spin
 *
 * Campaigns (each one a wheel), the spin log, and the analytics behind them.
 *
 * One thing every thunk here has to deal with: the shared `apiRequest` resolves
 * on failure as well as success — its catch returns `err.response.data`. So a
 * refusal arrives as an ordinary value with `status: "failed"`, and without
 * checking for it a rejected request would land in `fulfilled` and quietly wipe
 * good state with an empty payload. `unwrap` below turns that back into a
 * rejection, so `rejected` means what it says and the error message is the
 * server's own.
 */
// Lucky Spin's endpoints. Namespaced so other Magic Lamp tools can add their
// own under /magic-lamp without colliding.
const BASE = 'magic-lamp/spin';

const unwrap = (res) => {
  if (!res) throw new Error('No response from the server');
  if (res.status === 'failed' || res.status === 'error') {
    throw new Error(res.message || 'Something went wrong');
  }
  return res;
};

/** Drops empty filters so the query string only carries what was actually set. */
const clean = (params = {}) =>
  Object.fromEntries(
    Object.entries(params).filter(
      ([, value]) => value !== undefined && value !== null && value !== '',
    ),
  );

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

export const fetchCampaigns = createAsyncThunk(
  'magicLamp/fetchCampaigns',
  async () => unwrap(await apiRequest({ method: 'get', url: `${BASE}/campaigns` })),
);

export const fetchCampaign = createAsyncThunk(
  'magicLamp/fetchCampaign',
  async (id) => unwrap(await apiRequest({ method: 'get', url: `${BASE}/campaigns/${id}` })),
);

export const createCampaign = createAsyncThunk(
  'magicLamp/createCampaign',
  async (data) => unwrap(await apiRequest({ method: 'post', url: `${BASE}/campaigns`, data })),
);

export const saveCampaign = createAsyncThunk(
  'magicLamp/saveCampaign',
  async ({ id, ...data }) =>
    unwrap(await apiRequest({ method: 'put', url: `${BASE}/campaigns/${id}`, data })),
);

export const setCampaignStatus = createAsyncThunk(
  'magicLamp/setCampaignStatus',
  async ({ id, status }) =>
    unwrap(
      await apiRequest({
        method: 'post',
        url: `${BASE}/campaigns/${id}/status`,
        data: { status },
      }),
    ),
);

/**
 * The on/off switch. Its own thunk rather than part of saveCampaign, so
 * flipping a segment is one small write that does not depend on the rest of the
 * form being valid — it is what someone reaches for in a hurry when a prize
 * runs out mid-event.
 */
export const toggleSegment = createAsyncThunk(
  'magicLamp/toggleSegment',
  async ({ campaignId, segmentId, isActive }) =>
    unwrap(
      await apiRequest({
        method: 'patch',
        url: `${BASE}/campaigns/${campaignId}/segments/${segmentId}`,
        data: { isActive },
      }),
    ),
);

export const deleteCampaign = createAsyncThunk(
  'magicLamp/deleteCampaign',
  async (id) => {
    await unwrap(await apiRequest({ method: 'delete', url: `${BASE}/campaigns/${id}` }));
    return id;
  },
);

/** Short-lived link that opens the wheel on a tablet. */
/**
 * Uploads a prize picture through the portal's shared Contabo uploader and
 * returns its public URL.
 *
 * `headers: {}` is deliberate — the shared request helper defaults to
 * application/json, which would stop axios setting the multipart boundary and
 * the upload would arrive empty.
 */
export const uploadSegmentImage = createAsyncThunk(
  'magicLamp/uploadSegmentImage',
  async (file) => {
    const form = new FormData();
    form.append('files', file);
    form.append('folderName', 'magic-lamp');

    const res = await apiRequest({
      method: 'post',
      url: '/contabo-upload',
      data: form,
      headers: {},
    });

    const uploaded = res?.data?.[0];
    if (!uploaded?.file_key) throw new Error('Upload failed');
    // The key, not file_url. file_url is built from the portal's
    // CONTABO_PUBLIC_URL, which lacks the tenant prefix and 401s. Each front end
    // joins this key to its own base URL, as the refund module does.
    return uploaded.file_key;
  },
);

export const openWheel = createAsyncThunk(
  'magicLamp/openWheel',
  async (campaignId) =>
    unwrap(await apiRequest({ method: 'post', url: `${BASE}/open`, data: { campaignId } })),
);

// ---------------------------------------------------------------------------
// Analytics and the spin log
// ---------------------------------------------------------------------------

export const fetchAnalytics = createAsyncThunk(
  'magicLamp/fetchAnalytics',
  async (filters) =>
    unwrap(
      await apiRequest({ method: 'get', url: `${BASE}/analytics`, params: clean(filters) }),
    ),
);

export const fetchSpins = createAsyncThunk(
  'magicLamp/fetchSpins',
  async (params) =>
    unwrap(await apiRequest({ method: 'get', url: `${BASE}/spins`, params: clean(params) })),
);

export const voidSpin = createAsyncThunk(
  'magicLamp/voidSpin',
  async ({ id, reason }) =>
    unwrap(
      await apiRequest({ method: 'post', url: `${BASE}/spins/${id}/void`, data: { reason } }),
    ),
);

/**
 * Downloads the filtered log as CSV.
 *
 * Fetched as a blob rather than navigating to a URL: the portal's
 * authenticateUser reads the Bearer header, and a plain navigation cannot send
 * one. Nothing is stored in state — the file goes straight to the browser.
 */
export const exportSpins = createAsyncThunk(
  'magicLamp/exportSpins',
  async (filters) => {
    const blob = await apiRequest({
      method: 'get',
      url: `${BASE}/spins/export`,
      params: clean(filters),
      responseType: 'blob',
    });

    const url = window.URL.createObjectURL(new Blob([blob], { type: 'text/csv' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `lucky-spin-log-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    return true;
  },
);

// ---------------------------------------------------------------------------

const initialState = {
  campaigns: [],
  campaign: null,
  analytics: null,
  spins: [],
  spinsTotal: 0,

  // Separate flags rather than one: the campaign list, the editor and the log
  // are on different screens, and a shared flag makes one of them flicker when
  // another refetches.
  loadingCampaigns: false,
  loadingCampaign: false,
  loadingAnalytics: false,
  loadingSpins: false,
  saving: false,

  error: '',

  // Shared by Analytics and the spin log, so a filter set on one screen still
  // applies when you move to the other — they are two views of the same data.
  filters: { campaignId: '', from: '', to: '', result: '' },
};

const magicLampSlice = createSlice({
  name: 'magicLamp',
  initialState,
  reducers: {
    setFilters: (state, action) => ({ ...state, filters: { ...state.filters, ...action.payload } }),
    clearFilters: (state) => ({ ...state, filters: initialState.filters }),
    clearError: (state) => ({ ...state, error: '' }),
    /** Cleared on unmount so the editor never briefly shows the previous wheel. */
    clearCampaign: (state) => ({ ...state, campaign: null }),
  },

  extraReducers: (builder) => {
    // ------------------------------ campaigns ------------------------------
    builder.addCase(fetchCampaigns.pending, (state) => ({ ...state, loadingCampaigns: true }));
    builder.addCase(fetchCampaigns.fulfilled, (state, action) => ({
      ...state,
      campaigns: action.payload.data || [],
      loadingCampaigns: false,
    }));
    builder.addCase(fetchCampaigns.rejected, (state, action) => ({
      ...state,
      loadingCampaigns: false,
      error: action.error.message,
    }));

    builder.addCase(fetchCampaign.pending, (state) => ({ ...state, loadingCampaign: true }));
    builder.addCase(fetchCampaign.fulfilled, (state, action) => ({
      ...state,
      campaign: action.payload.data,
      loadingCampaign: false,
    }));
    builder.addCase(fetchCampaign.rejected, (state, action) => ({
      ...state,
      loadingCampaign: false,
      error: action.error.message,
    }));

    builder.addCase(createCampaign.pending, (state) => ({ ...state, saving: true }));
    builder.addCase(createCampaign.fulfilled, (state, action) => ({
      ...state,
      campaign: action.payload.data,
      saving: false,
    }));
    builder.addCase(createCampaign.rejected, (state, action) => ({
      ...state,
      saving: false,
      error: action.error.message,
    }));

    // saveCampaign and toggleSegment both return the whole campaign, so the
    // editor always renders exactly what the server stored rather than an
    // optimistic guess at it.
    builder.addCase(saveCampaign.pending, (state) => ({ ...state, saving: true }));
    builder.addCase(saveCampaign.fulfilled, (state, action) => ({
      ...state,
      campaign: action.payload.data,
      saving: false,
    }));
    builder.addCase(saveCampaign.rejected, (state, action) => ({
      ...state,
      saving: false,
      error: action.error.message,
    }));

    builder.addCase(toggleSegment.fulfilled, (state, action) => ({
      ...state,
      campaign: action.payload.data,
    }));
    builder.addCase(toggleSegment.rejected, (state, action) => ({
      ...state,
      error: action.error.message,
    }));

    builder.addCase(setCampaignStatus.rejected, (state, action) => ({
      ...state,
      error: action.error.message,
    }));

    builder.addCase(deleteCampaign.fulfilled, (state, action) => ({
      ...state,
      campaigns: state.campaigns.filter((c) => c.id !== action.payload),
    }));
    builder.addCase(deleteCampaign.rejected, (state, action) => ({
      ...state,
      error: action.error.message,
    }));

    builder.addCase(openWheel.rejected, (state, action) => ({
      ...state,
      error: action.error.message,
    }));

    // ------------------------------ analytics ------------------------------
    builder.addCase(fetchAnalytics.pending, (state) => ({ ...state, loadingAnalytics: true }));
    builder.addCase(fetchAnalytics.fulfilled, (state, action) => ({
      ...state,
      analytics: action.payload.data,
      loadingAnalytics: false,
    }));
    builder.addCase(fetchAnalytics.rejected, (state, action) => ({
      ...state,
      loadingAnalytics: false,
      error: action.error.message,
    }));

    // ------------------------------ spin log -------------------------------
    builder.addCase(fetchSpins.pending, (state) => ({ ...state, loadingSpins: true }));
    builder.addCase(fetchSpins.fulfilled, (state, action) => ({
      ...state,
      spins: action.payload.data?.spins || [],
      spinsTotal: action.payload.data?.total || 0,
      loadingSpins: false,
    }));
    builder.addCase(fetchSpins.rejected, (state, action) => ({
      ...state,
      loadingSpins: false,
      error: action.error.message,
    }));

    builder.addCase(voidSpin.rejected, (state, action) => ({
      ...state,
      error: action.error.message,
    }));
  },
});

export const { setFilters, clearFilters, clearError, clearCampaign } = magicLampSlice.actions;

export default magicLampSlice.reducer;
